'use client';

import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Sidebar } from './sidebar';
import { ScrollFx } from '../ui/scroll-fx';

interface MainLayoutProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Main layout component
 * Provides the overall layout structure with fixed sidebar and main content area
 * Responsive design with mobile, tablet and desktop support
 * Optimized for the new compact sidebar design
 */
export function MainLayout({ children, className }: MainLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when ESC key is pressed
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscKey);

    // Prevent body scroll when menu is open
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      window.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const sidebarVariants = {
    hidden: {
      x: -250,  // Change from 250 to -250
      opacity: 0,
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.05,
      },
    },
    exit: {
      x: -250,  // Change from 250 to -250
      opacity: 0,
      transition: {
        duration: 0.2,
        when: "afterChildren",
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  };

  return (
    <div className="flex min-h-screen relative bg-background overflow-x-hidden">
      {/* Sidebar handles both desktop and mobile drawer internally */}
      <Sidebar
        onMobileMenuOpen={() => setIsMobileMenuOpen(true)}
        isMobile={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />

      {/* Main content — top padding clears the fixed navbar */}
      <main className={cn(
        'flex-1 px-4 sm:px-5 md:px-6 pb-4 sm:pb-5 md:pb-6 pt-[110px]',
        'bg-background text-foreground overflow-x-hidden',
        className
      )}>
        <ScrollFx />
        {children}
      </main>
    </div>
  );
}
